import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppWindow, Save, X } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useBlocker, useNavigate, useParams } from 'react-router';
import { ModelImage } from '../../components/model-image';
import { Combobox } from '../../components/pickers';
import { EMPTY_DOCUMENT, isDocumentEmpty, RichTextEditor, type RichTextEditorHandle } from '../../components/rich-text';
import { useToast } from '../../components/toast';
import { Alert, Button, Card, CardHeader, cn, ConfirmDialog, ErrorState, Field, Input, PageHeader, PageLoader, Textarea } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import {
  EXCLUSIVE_VISIBILITY,
  knowledgeImageUrl,
  toggleVisibility,
  uploadKnowledgeImage,
  VISIBILITY_OPTIONS,
  type KnowledgeArticle,
  type KnowledgeModelTag,
  type KnowledgeTagCounts,
  type KnowledgeVisibility,
} from '../../lib/knowledge';
import { useCatalog } from '../../lib/parts';
import { useSoftwareList, type SoftwareOption } from '../../lib/software';

const MAX_TAGS = 20;

/** /knowledge/new and /knowledge/:id/edit: technicians and administrators write articles here. */
export function KnowledgeEditorPage() {
  const { id } = useParams();
  const existing = useQuery({ queryKey: ['knowledge', 'article', id], queryFn: () => api.get<KnowledgeArticle>(`/knowledge/${id}`), enabled: !!id });
  if (id && existing.isLoading) return <PageLoader />;
  if (id && (existing.error || !existing.data)) return <ErrorState error={existing.error} onRetry={() => existing.refetch()} />;
  return <ArticleForm key={id ?? 'new'} article={existing.data ?? null} />;
}

/** Free-text tags: type one and press Enter (or a comma). Suggestions come from other articles. */
function TagInput({ label, values, onChange, suggestions, placeholder }: { label: string; values: string[]; onChange(values: string[]): void; suggestions: string[]; placeholder: string }) {
  const [draft, setDraft] = useState('');
  const listId = useId();
  const has = (tag: string) => values.some((value) => value.toLowerCase() === tag.toLowerCase());

  const add = (raw: string) => {
    const tag = raw.replace(/\s+/g, ' ').trim().slice(0, 60);
    setDraft('');
    if (!tag || has(tag) || values.length >= MAX_TAGS) return;
    onChange([...values, suggestions.find((known) => known.toLowerCase() === tag.toLowerCase()) ?? tag]);
  };

  return (
    <Field as="div" label={label} hint={values.length >= MAX_TAGS ? `Up to ${MAX_TAGS}` : 'Press Enter after each one'}>
      {values.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {values.map((value) => (
            <span key={value} className="inline-flex max-w-full items-center gap-1 rounded-full bg-slate-100 py-0.5 pr-1 pl-2.5 text-xs text-slate-700">
              <span className="truncate">{value}</span>
              <button type="button" aria-label={`Remove ${value}`} onClick={() => onChange(values.filter((other) => other !== value))} className="rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700">
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Input
        list={listId}
        value={draft}
        aria-label={label}
        placeholder={placeholder}
        maxLength={61}
        disabled={values.length >= MAX_TAGS}
        onChange={(event) => {
          const value = event.target.value;
          if (value.endsWith(',')) add(value.slice(0, -1));
          else setDraft(value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            add(draft);
          } else if (event.key === 'Backspace' && !draft && values.length > 0) {
            onChange(values.slice(0, -1));
          }
        }}
        onBlur={() => add(draft)}
      />
      <datalist id={listId}>
        {suggestions
          .filter((tag) => !has(tag))
          .map((tag) => (
            <option key={tag} value={tag} />
          ))}
      </datalist>
    </Field>
  );
}

/** Device tags, chosen from Makes & models. */
function DeviceTags({ values, onChange }: { values: KnowledgeModelTag[]; onChange(values: KnowledgeModelTag[]): void }) {
  const catalog = useCatalog();
  const [query, setQuery] = useState('');
  const options = useMemo(
    () =>
      (catalog.data ?? [])
        .flatMap((maker) => maker.models.map((model) => ({ id: model.id, name: `${maker.name} ${model.name}`, imageUrl: model.imageUrl })))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [catalog.data],
  );
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matches = options.filter((option) => !values.some((value) => value.id === option.id) && words.every((word) => option.name.toLowerCase().includes(word))).slice(0, 8);

  return (
    <Field as="div" label="Devices" hint={catalog.data && options.length === 0 ? 'Add models under Settings → Makes & models to tag devices.' : undefined}>
      {values.length > 0 && (
        <ul className="mb-2 space-y-1.5">
          {values.map((value) => (
            <li key={value.id} className="flex items-center gap-2 text-sm text-slate-800">
              <ModelImage url={value.imageUrl} alt="" className="size-8" />
              <span className="min-w-0 flex-1 truncate">{value.name}</span>
              <button type="button" aria-label={`Remove ${value.name}`} onClick={() => onChange(values.filter((other) => other.id !== value.id))} className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {values.length < MAX_TAGS && options.length > 0 && (
        <Combobox
          showWhenEmpty
          query={query}
          onQueryChange={setQuery}
          loading={catalog.isLoading}
          items={matches}
          getKey={(option) => option.id}
          placeholder="Search makes & models"
          emptyText="No models match"
          onSelect={(option) => {
            onChange([...values, option]);
            setQuery('');
          }}
          renderItem={(option) => (
            <>
              <ModelImage url={option.imageUrl} alt="" className="size-7" />
              <span className="min-w-0 truncate">{option.name}</span>
            </>
          )}
        />
      )}
    </Field>
  );
}

type SoftwareTag = Pick<SoftwareOption, 'id' | 'name'>;

/** Software tags, chosen from the school's software list (Settings → Software). */
function SoftwareTags({ values, onChange }: { values: SoftwareTag[]; onChange(values: SoftwareTag[]): void }) {
  const list = useSoftwareList();
  const [query, setQuery] = useState('');
  const options = list.data ?? [];
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matches = options
    .filter((option) => !values.some((value) => value.id === option.id))
    .filter((option) => words.every((word) => `${option.name} ${option.publisher ?? ''}`.toLowerCase().includes(word)))
    .slice(0, 8);

  return (
    <Field
      as="div"
      label="Software"
      hint={
        list.data && options.length === 0 ? (
          <>
            Add software under{' '}
            <Link to="/settings?tab=software" className="font-medium text-brand-700 hover:underline">
              Settings → Software
            </Link>{' '}
            to tag it.
          </>
        ) : undefined
      }
    >
      {values.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {values.map((value) => (
            <span key={value.id} className="inline-flex max-w-full items-center gap-1 rounded-full bg-slate-100 py-0.5 pr-1 pl-2 text-xs text-slate-700">
              <AppWindow className="size-3 shrink-0 text-slate-400" />
              <span className="truncate">{value.name}</span>
              <button type="button" aria-label={`Remove ${value.name}`} onClick={() => onChange(values.filter((other) => other.id !== value.id))} className="rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700">
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {values.length < MAX_TAGS && options.length > 0 && (
        <Combobox
          showWhenEmpty
          query={query}
          onQueryChange={setQuery}
          loading={list.isLoading}
          items={matches}
          getKey={(option) => option.id}
          placeholder="Search the software list"
          emptyText="No software matches"
          onSelect={(option) => {
            onChange([...values, { id: option.id, name: option.name }]);
            setQuery('');
          }}
          renderItem={(option) => (
            <>
              <AppWindow className="size-4 shrink-0 text-slate-400" />
              <span className="min-w-0 truncate">{option.name}</span>
              {option.publisher && <span className="ml-auto shrink-0 text-xs text-slate-400">{option.publisher}</span>}
            </>
          )}
        />
      )}
    </Field>
  );
}

function ArticleForm({ article }: { article: KnowledgeArticle | null }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const editor = useRef<RichTextEditorHandle>(null);
  const bodyLabel = useId();
  const tags = useQuery({ queryKey: ['knowledge', 'tags'], queryFn: () => api.get<KnowledgeTagCounts>('/knowledge/tags'), staleTime: 60_000 });

  const [title, setTitle] = useState(article?.title ?? '');
  const [summary, setSummary] = useState(article?.summary ?? '');
  const [visibility, setVisibility] = useState<KnowledgeVisibility[]>(article?.visibility ?? ['INTERNAL']);
  const [models, setModels] = useState<KnowledgeModelTag[]>(article?.models ?? []);
  const [software, setSoftware] = useState<SoftwareTag[]>(article?.software ?? []);
  const [topics, setTopics] = useState(article?.topics ?? []);
  const [error, setError] = useState('');
  const initial = useMemo(() => article?.body ?? EMPTY_DOCUMENT, [article]);

  // Unsaved changes: leaving asks first. Refs, so a save can leave right away.
  const dirty = useRef(false);
  const leaving = useRef(false);
  const markDirty = () => {
    dirty.current = true;
  };
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty.current && !leaving.current && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (dirty.current && !leaving.current) event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  const back = article ? `/knowledge/${article.id}` : '/knowledge';
  const change =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      markDirty();
      set(value);
    };

  const save = useMutation({
    mutationFn: async () => {
      const body = editor.current?.getDocument();
      if (!body || isDocumentEmpty(body)) throw new Error('Write the article before saving it');
      const payload = { title, summary, visibility, body, modelIds: models.map((model) => model.id), softwareIds: software.map((entry) => entry.id), topics };
      return article ? api.put<KnowledgeArticle>(`/knowledge/${article.id}`, payload) : api.post<KnowledgeArticle>('/knowledge', payload);
    },
    onSuccess: (saved) => {
      leaving.current = true;
      queryClient.setQueryData(['knowledge', 'article', saved.id], saved);
      void queryClient.invalidateQueries({ queryKey: ['knowledge'] });
      toast.success(article ? 'Article saved' : 'Article created');
      navigate(`/knowledge/${saved.id}`);
    },
    onError: (err) => {
      setError(errorMessage(err));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  const submit = () => {
    setError('');
    if (title.trim().length < 3) {
      setError('Give the article a title');
      return;
    }
    if (visibility.length === 0) {
      setError('Choose who can read it');
      return;
    }
    save.mutate();
  };

  return (
    <>
      <PageHeader
        back={{ to: back, label: article ? 'Back to the article' : 'Knowledgebase' }}
        title={article ? 'Edit article' : 'New article'}
        actions={
          <>
            <Button onClick={() => navigate(back)}>Cancel</Button>
            <Button variant="primary" icon={<Save />} loading={save.isPending} onClick={submit}>
              Save article
            </Button>
          </>
        }
      />
      {error && (
        <Alert tone="red" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Not a form: Enter in a field (or in the editor's dialogs) never saves the article. */}
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <Card className="min-w-0 space-y-4 p-5">
          <Field label="Title" required>
            <Input value={title} onChange={(event) => change(setTitle)(event.target.value)} maxLength={200} required placeholder="e.g. Connecting to the school Wi-Fi" className="text-base font-medium" />
          </Field>
          <Field label="Summary" hint="Optional - a sentence shown in the list and under the title">
            <Textarea rows={2} maxLength={500} value={summary} onChange={(event) => change(setSummary)(event.target.value)} />
          </Field>
          <div>
            <p id={bodyLabel} className="mb-1 block text-sm font-medium text-slate-700">
              Article <span className="text-red-500">*</span>
            </p>
            <RichTextEditor
              ref={editor}
              initial={initial}
              imageSrc={knowledgeImageUrl}
              uploadImage={uploadKnowledgeImage}
              onChange={markDirty}
              onError={(message) => toast.error(message)}
              labelledBy={bodyLabel}
              placeholder="Write the steps, answer, or notes. Paste or drop screenshots right in."
            />
          </div>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Who can read it" description="Students and teachers & staff can both be ticked. The technology team can read those too." />
            <fieldset className="space-y-1 p-2">
              <legend className="sr-only">Who can read it</legend>
              {VISIBILITY_OPTIONS.map((option, index) => (
                <label
                  key={option.value}
                  className={cn(
                    'flex cursor-pointer gap-3 rounded-lg px-3 py-2',
                    visibility.includes(option.value) ? 'bg-brand-50 ring-1 ring-brand-200' : 'hover:bg-slate-50',
                    // Students and teachers & staff sit together, between the choices that stand alone.
                    index > 0 && EXCLUSIVE_VISIBILITY.includes(option.value) && 'mt-2',
                  )}
                >
                  <input
                    type="checkbox"
                    name="visibility"
                    value={option.value}
                    checked={visibility.includes(option.value)}
                    onChange={() => change(setVisibility)(toggleVisibility(visibility, option.value))}
                    className="mt-1 size-4 shrink-0 rounded border-slate-300 accent-brand-700"
                  />
                  <span>
                    <span className="block text-sm font-medium text-slate-900">{option.label}</span>
                    <span className="block text-xs text-slate-500">{option.description}</span>
                  </span>
                </label>
              ))}
            </fieldset>
          </Card>

          <Card>
            <CardHeader title="Tags" description="Help people find it by device, software, or topic" />
            <div className="space-y-4 p-5">
              <DeviceTags values={models} onChange={change(setModels)} />
              <SoftwareTags values={software} onChange={change(setSoftware)} />
              <TagInput label="Topics" values={topics} onChange={change(setTopics)} suggestions={tags.data?.topics.map((tag) => tag.value) ?? []} placeholder="e.g. Wi-Fi, Printing" />
            </div>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()}
        onConfirm={() => {
          leaving.current = true;
          blocker.proceed?.();
        }}
        title="Leave without saving?"
        description="Your changes to this article haven’t been saved."
        confirmLabel="Leave"
      />
    </>
  );
}
